import { useCallback, useRef } from 'react';
import {
  useBulkOperationsOptional,
  type BulkActivityType,
} from './BulkOperationsContext';
import type { BulkOperationItemMeta } from './BulkOperationQueue.interface';

interface UseBulkOperationOptions {
  activityType: BulkActivityType;
  title?: string;
}

interface BulkOperationResult {
  /** Start the bulk operation tracking - call before API request */
  startTracking: (total: number, meta?: BulkOperationItemMeta) => void;
  /** Complete with success - call when API returns success */
  completeSuccess: (successCount: number) => void;
  /** Complete with errors - call when API returns errors */
  completeWithErrors: (params: {
    successCount: number;
    failedCount: number;
    errors: Array<{ id: string | number; message: string }>;
  }) => void;
  /** Complete with full failure - call when API fails completely */
  completeFailed: (errorMessage?: string) => void;
  /** Update progress during operation */
  updateProgress: (
    processed: number,
    successCount?: number,
    failedCount?: number
  ) => void;
}

/**
 * Hook for tracking bulk operations in the BulkOperationQueue.
 *
 * @example
 * ```tsx
 * const { startTracking, completeSuccess, completeWithErrors } = useBulkOperation({
 *   activityType: 'add_to_project',
 *   title: 'Adding to Software Tester Project',
 * });
 *
 * const handleBulkAction = async () => {
 *   startTracking(selectedCandidates.length);
 *
 *   const result = await dispatch(bulkAddToProject(...));
 *
 *   if (result.errors?.length) {
 *     completeWithErrors({
 *       successCount: result.data?.length || 0,
 *       failedCount: result.errors.length,
 *       errors: result.errors.map(e => ({ id: e.id, message: e.message })),
 *     });
 *   } else {
 *     completeSuccess(result.data?.length || selectedCandidates.length);
 *   }
 * };
 * ```
 */
export const useBulkOperation = (
  options: UseBulkOperationOptions
): BulkOperationResult => {
  const bulkOps = useBulkOperationsOptional();
  const operationIdRef = useRef<string | null>(null);

  const startTracking = useCallback(
    (total: number, meta?: BulkOperationItemMeta) => {
      if (!bulkOps) return;

      operationIdRef.current = bulkOps.startOperation({
        activityType: options.activityType,
        title: options.title,
        total,
        meta,
      });

      // DON'T trigger bulk activities fetch here - it should be triggered AFTER
      // the main action API call succeeds, not before.
      // The local operation shows the modal immediately, and triggerBulkActivitiesFetch
      // should be called in completeSuccess/completeWithErrors/completeFailed after API response.
    },
    [bulkOps, options.activityType, options.title]
  );

  const completeSuccess = useCallback(
    (_successCount: number) => {
      if (!bulkOps) return;

      // For server-side bulk operations, DON'T mark local operation as completed
      // and DON'T remove it immediately. Keep it as "in_progress" so the modal stays visible.
      // The API polling will fetch the actual server-side status.
      // Once API data is available, the deduplication logic in BulkOperationsContext
      // will automatically skip the local operation in favor of API data.
      // This prevents both the "completed then loading" flash AND the modal disappearing.

      // Just trigger bulk activities fetch - keep local operation as-is
      bulkOps.triggerBulkActivitiesFetch();
    },
    [bulkOps]
  );

  const completeWithErrors = useCallback(
    (_params: {
      successCount: number;
      failedCount: number;
      errors: Array<{ id: string | number; message: string }>;
    }) => {
      if (!bulkOps) return;

      // For server-side bulk operations, DON'T mark local operation as completed
      // and DON'T remove it immediately. Keep it as "in_progress" so the modal stays visible.
      // The API polling will fetch the actual server-side status.
      // Once API data is available, the deduplication logic in BulkOperationsContext
      // will automatically skip the local operation in favor of API data.
      // This prevents both the "completed then loading" flash AND the modal disappearing.

      // Just trigger bulk activities fetch - keep local operation as-is
      bulkOps.triggerBulkActivitiesFetch();
    },
    [bulkOps]
  );

  const completeFailed = useCallback(
    (_errorMessage?: string) => {
      if (!bulkOps) return;

      // For server-side bulk operations, DON'T mark local operation as failed
      // and DON'T remove it immediately. Keep it as "in_progress" so the modal stays visible.
      // The API polling will fetch the actual server-side status.
      // Once API data is available, the deduplication logic in BulkOperationsContext
      // will automatically skip the local operation in favor of API data.
      // This prevents both the "completed/failed then loading" flash AND the modal disappearing.

      // Just trigger bulk activities fetch - keep local operation as-is
      bulkOps.triggerBulkActivitiesFetch();
    },
    [bulkOps]
  );

  const updateProgress = useCallback(
    (processed: number, successCount?: number, failedCount?: number) => {
      if (!bulkOps || !operationIdRef.current) return;

      bulkOps.updateOperation(operationIdRef.current, {
        processed,
        successCount,
        failedCount,
      });
    },
    [bulkOps]
  );

  return {
    startTracking,
    completeSuccess,
    completeWithErrors,
    completeFailed,
    updateProgress,
  };
};
